import { CreateLocationDataSourceModel, ILocationDataSources, LocationDataSourceModel, UpdateLocationDataSourceModel } from '@truckdown/locations';
import context from '@truckdown/systems';
import { defineComponent, ref, Ref } from 'vue';
import { showMessage } from '../Utilities/utilities';
import validationError from '../Validation/Error/Error.vue';
import { DataSourceValidator } from './Validation/DataSourceValidator';

interface IProps {
    dataSources: LocationDataSourceModel[];
    locationId: string;
}

interface IData {
    isLoading: Ref<boolean>,
    pageCount: Ref<number>,
    resetList: Ref<boolean>,
    showDeleteModal: Ref<boolean>,
    showEditModal: Ref<boolean>,
    showAddModal: Ref<boolean>,
    editedSource: Ref<CreateLocationDataSourceModel | UpdateLocationDataSourceModel | undefined>,
    sourceToDelete: Ref<string | undefined>,
    dataSources: Ref<LocationDataSourceModel[]>,
    dataSourceValidator: Ref<any>,
}

const getData = function (props: IProps): IData {
    return {
        isLoading: ref<boolean>(false),
        pageCount: ref<number>(10),
        resetList: ref<boolean>(false),
        showDeleteModal: ref<boolean>(false),
        showEditModal: ref<boolean>(false),
        showAddModal: ref<boolean>(false),
        editedSource: ref<CreateLocationDataSourceModel | UpdateLocationDataSourceModel | undefined>(),
        sourceToDelete: ref<string | undefined>(),
        dataSources: ref<LocationDataSourceModel[]>(props.dataSources),
        dataSourceValidator: ref<any>(new DataSourceValidator(undefined)),
    };
}

const isSourceValid = function (data: IData): boolean {
    data.dataSourceValidator.value = new DataSourceValidator(data.editedSource.value);
    return data.dataSourceValidator.value.validate();
}

const getMethods = function (props: IProps, data: IData, ctx: any) {
   
    const updateItem = function (result: LocationDataSourceModel) {
        if (data.editedSource.value != undefined && result) {
            const index = data.dataSources.value.findIndex(i => i.id === (data.editedSource.value as UpdateLocationDataSourceModel).id);
            if (index !== -1) {
                data.dataSources.value[index] = result;
            }
        }
    };

    const removeItem = function () {
        data.dataSources.value = data.dataSources.value.filter(item => item.id !== data.sourceToDelete.value);
    };
    return {
        openEditModal: function (source: LocationDataSourceModel) {
            data.showEditModal.value = true;
            data.editedSource.value = { ...source, locationId: props.locationId } as UpdateLocationDataSourceModel;
            data.dataSourceValidator.value = new DataSourceValidator(data.editedSource.value)
        },
        openAddModal: function () {
            data.showAddModal.value = true;
            data.editedSource.value = {
                locationId: props.locationId,
                key: "",
                source: "",
                websiteAddress: ""
            }
            data.dataSourceValidator.value = new DataSourceValidator(data.editedSource.value);
        },
        openDeleteModal: function (source: LocationDataSourceModel) {
            data.showDeleteModal.value = true;
            data.sourceToDelete.value = source.id;
        },
        closeEditModal: function () {
            data.showEditModal.value = false;
            data.showAddModal.value = false;
            data.editedSource.value = undefined;
        },
        closeDeleteModal: function () {
            data.showDeleteModal.value = false;
            data.sourceToDelete.value = undefined;
        },
        confirmSave: async function () {
            if (data.showAddModal.value && data.editedSource.value) {
                if (isSourceValid(data)) {
                    try {
                        const manager = await context.getService<ILocationDataSources>('ILocationDataSources');
                        let result = await manager.createLocationDataSource(data.editedSource.value as CreateLocationDataSourceModel);
                        if (result && result.ok && result.data) {
                            data.showAddModal.value = false;
                            data.dataSources.value.push(result.data);
                            data.editedSource.value = undefined;
                            showMessage(`Listing data source was succesfully created.`, "success", true, true);
                        } else {
                            showMessage(`An error occurred while adding the data source.`, "danger", true, true);
                        }
                    } catch (error) {
                        console.error('Error adding location data source:', error);
                    }
                }
            } else if (data.editedSource.value) {
                if (isSourceValid(data)) {
                    const manager = await context.getService<ILocationDataSources>('ILocationDataSources');
                    try {
                        let result = await manager.updateLocationDataSource(data.editedSource.value as UpdateLocationDataSourceModel);
                        if (result && result.ok && result.data != undefined) {
                            updateItem(result.data);
                            data.editedSource.value = undefined;
                            data.showEditModal.value = false;
                            showMessage(`Listing data source was succesfully updated.`, "success", true, true);
                        } else {
                            showMessage(`An error occurred while updating the data source.`, "danger", true, true);
                        }
                    } catch (error) {
                        console.error('Error updating location data source:', error);
                    }
                }
            }
        },
        confirmDelete: async function () {
            if (data.sourceToDelete.value) {
                const manager = await context.getService<ILocationDataSources>('ILocationDataSources');
                try {
                    let result = await manager.removeLocationDataSource(data.sourceToDelete.value, props.locationId);
                    if (result && result.ok) {
                        removeItem();
                        data.sourceToDelete.value = "";
                        data.showDeleteModal.value = false;
                        showMessage(`Listing data source was succesfully deleted.`, "success", true, true);
                    } else {
                        showMessage(`An error occurred while deleting the data source.`, "danger", true, true);
                    }
                } catch (error) {
                    console.error('Error deleting location data source:', error);
                }
            }
        }
    }
}

export default defineComponent({
    name: "data-sources",
    props: {
        dataSources: {
            type: Array as () => LocationDataSourceModel[],
            required: true,
        },
        locationId: {
            type: String,
            required: true
        }
    },
    components: {
        "error": validationError
    },
    setup: function (props, ctx) {
        let data = getData(props);
        let methods = getMethods(props, data, ctx);

        return {
            ...data,
            ...methods
        };
    }
});